<template>
    <div>
        <v-server-table :name="tableName" :ref="tableName" url="/employees/list" :columns="columns" :options="options">

            <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                <div class="clearfix d-md-none d-block"></div>

                <div v-if="allowFilterSaving" class="float-left mr-1">
                    <user-filters ref="empSavedFilters" assetType="Employee" :filterKey="filterKey" @saving="setUserFilterData" @show="setUserFilterData"></user-filters>
                </div>

                <div class="float-left">
                    <table-filters-2 ref="empTableFilters"
                        v-bind:filters="filters"
                        :emptyFilters="filterDefs"
                        :filterKey="filterKey"
                        filterUrl="/employees/filters"
                        @filtersChanged="refreshTableData"
                        @filtersCleared="filtersCleared"
                        v-show="!dashboardFilters">
                        <template slot-scope="slotProps">
                            <div class="d-inline-block mb-1">
                                <multi-select v-model="slotProps.filters['employee.current_jobsite_id']" search :btnLabel="(values) => ('Current Jobsites ('+values.length+')')" :selectOptions="slotProps.filterData.jobsites" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block mb-1">
                                <multi-select v-model="slotProps.filters['employee.subcontractor_id']" search :btnLabel="(values) => ('Subcontractors ('+values.length+')')" :selectOptions="slotProps.filterData.subcontractors" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block mb-1">
                                <multi-select v-model="slotProps.filters['employee.driver_status_id']" :btnLabel="(values) => ('Approved Driver ('+values.length+')')" :selectOptions="slotProps.filterData.drivers" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block mb-1">
                                <multi-select v-model="slotProps.filters['employee.IsActive']" :btnLabel="(values) => ('Active Status ('+values.length+')')" :selectOptions="isActiveOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block mb-1">
                                <multi-select v-model="slotProps.filters['employee.is_temp_employee']" :btnLabel="(values) => ('Permanent/Temp ('+values.length+')')" :selectOptions="empTypeOptions" :options="{multi: false}"></multi-select>
                            </div>
                            <div class="d-inline-block mb-1">
                                <multi-select v-model="slotProps.filters['employee.division_id']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block mb-1">
                                <multi-select v-model="slotProps.filters['employee.position']" search :btnLabel="(values) => ('Positions ('+values.length+')')" :selectOptions="slotProps.filterData.positions" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block mb-1">
                                <date-range-selector buttonTitle="Hire Date" v-model="slotProps.filters['employee.HireDate']" />
                            </div>
                            <div class="d-inline-block mb-1">
                                <date-range-selector buttonTitle="Last Rehire Date" v-model="slotProps.filters['employee.last_rehire_date']" />
                            </div>
                            <template v-if="slotProps.filterData.custom_fields.length > 0">
                                <hr/>
                                <h5>Custom Fields <i class="fa fa-filter"></i></h5>

                                <div class="d-inline-block mr-2 mb-2" v-for="field in slotProps.filterData.custom_fields">
                                    <template v-if="field.type == 'Text' || field.type == 'Email' || field.type == 'Phone' || field.type == 'Textarea'">
                                        <b-form-input v-model="slotProps.filters['custom_fields'][field.id]" :placeholder="field.label" size="sm"></b-form-input>
                                    </template>

                                    <template v-else-if="field.type == 'Number'">
                                        <b-input-group>
                                            <b-input-group-prepend>
                                                <b-form-select v-model="slotProps.filters['custom_fields'][field.id+'_logic']" :options="logicOptions" size="sm"></b-form-select>
                                            </b-input-group-prepend>
                                            <b-form-input v-model="slotProps.filters['custom_fields'][field.id+'_value']" :placeholder="field.label" size="sm" type="number"></b-form-input>
                                        </b-input-group>
                                    </template>

                                    <template v-else-if="field.type == 'Dropdown' || field.type == 'Radio' || field.type == 'Checkbox'">
                                        <multi-select v-model="slotProps.filters['custom_fields'][field.id]" :btnLabel="(values) => (field.label + ' ('+values.length+')')" :selectOptions="field.field_options" :options="{multi: true, labelName: 'label'}"></multi-select>
                                    </template>

                                    <template v-else-if="field.type == 'Date'">
                                        <date-range-selector :buttonTitle="field.label" v-model="slotProps.filters['custom_fields'][field.id]" />
                                    </template>

                                    <template v-else-if="field.type == 'File'">
                                        <multi-select v-model="slotProps.filters['custom_fields'][field.id]"
                                            :btnLabel="(values) => (field.label + ' ('+values.length+')')"
                                            :selectOptions="[{id: 1, name: 'Has '+field.label+' File'}, {id: 0, name: 'Does Not Have '+field.label+' File'}]"
                                            :options="{multi: false}"></multi-select>
                                    </template>
                                </div>
                            </template>
                        </template>
                    </table-filters-2>
                </div>

                <button type="button" class="btn btn-outline-success ml-2" v-on:click="exportTable()" :disabled="exportingTable">
                    <template v-if="!exportingTable"><i class="fa fa-file-excel-o"></i> Export</template>
                    <template v-else><i class="fa fa-circle-o-notch fa-spin"></i> Exporting</template>
                </button>

                <div class="clearfix d-md-none d-block"></div>
            </div>

            <router-link slot="LastName" slot-scope="props" :to="`/employees/view/${props.row.EmployeeId}`" title="View Employee">
                {{ props.row.FirstName }} {{ props.row.LastName }}
            </router-link>

            <template slot="IsActive" slot-scope="props">
                <label class="badge badge-success" v-if="props.row.IsActive == 1">Active</label>
                <label class="badge badge-warning" v-else>Inactive</label>
            </template>

            <template slot="driver_status_id" slot-scope="props">
                <label class="badge badge-success" v-if="props.row.driver_status_id == 1">{{ props.row.driver_status_name }}</label>
                <label class="badge badge-warning" v-else-if="props.row.driver_status_id == 2">{{ props.row.driver_status_name }}</label>
                <label class="badge badge-info" v-else-if="props.row.driver_status_id">{{ props.row.driver_status_name }}</label>
                <span v-else>--</span>
            </template>

            <template slot="current_jobsite_id" slot-scope="props">
                <template v-if="props.row.current_jobsite_id">
                    <router-link v-if="$root.hasPermission('jobsites.view')" :to="`/jobsites/view/${props.row.current_jobsite_id}`" title="View Jobsite">
                        {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                    </router-link>
                    <span v-else>
                        {{ props.row.JobsiteNumber }} - {{ props.row.JobsiteName }}
                    </span>

                    <span v-if="props.row.last_checked_in_at" title="Check In Timestamp">
                        &nbsp;-&nbsp;{{ props.row.last_checked_in_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}
                    </span>
                </template>
                <span v-else>
                    --
                </span>
            </template>

            <template slot="division_id" slot-scope="props">
                <template v-if="props.row.division_id">
                    <span v-if="props.row.division_code">{{ props.row.division_code }} - </span>{{ props.row.DivisionName }}
                </template>
                <span v-else>--</span>
            </template>

            <template slot="child_row" slot-scope="props">
                <div class="mb-1">
                    <label class="text-muted">Trade:</label> {{ props.row.trade }}
                </div>
                <div class="mb-1">
                    <label class="text-muted">Hire Date:</label> <span v-if="props.row.HireDate">{{ props.row.HireDate | moment("MM/DD/YYYY") }}</span>
                </div>
                <div class="mb-1">
                    <label class="text-muted">Last Rehire Date:</label> <span v-if="props.row.last_rehire_date">{{ props.row.last_rehire_date | moment("MM/DD/YYYY") }}</span>
                </div>

                <div class="mb-1">
                    <label class="text-muted">Address:</label><br/>
                    {{ props.row.Address }}<br v-if="props.row.Address" />
                    {{ props.row.City }}<span v-if="props.row.City && props.row.State">,</span> {{ props.row.State }} {{ props.row.Zip }}
                </div>

                <template v-if="props.row.emp_custom_fields">
                    <div class="mb-1" v-for="field in JSON.parse(props.row.emp_custom_fields)">
                        <label class="text-muted">{{ field.label }}:</label>
                        <span v-if="field.value.EmployeeId">{{ field.value.FirstName }} {{ field.value.LastName }} - {{ field.value.EmployeeNumber }}</span>
                        <span v-else-if="field.value.UserId">{{ field.value.FirstName }} {{ field.value.LastName }} - {{ field.value.email }}</span>
                        <span v-else-if="field.value.JobsiteId">{{ field.value.JobsiteNumber }} - {{ field.value.JobsiteName }}</span>
                        <span v-else-if="field.value.equipment_id">
                            {{ field.value.equipment_type_name }}
                            <span v-if="field.value.equipment_name">-</span> {{ field.value.equipment_name }}
                            <span v-if="field.value.model_number">-</span> {{ field.value.model_number }}
                            <span v-if="field.value.serial_number">-</span> {{ field.value.serial_number }}
                        </span>
                        <div v-else-if="field.type === 'Textarea'" v-html="field.value"></div>
                        <span v-else-if="Array.isArray(field.value)">{{ field.value.join(', ') }}</span>
                        <span v-else>{{ field.value }}</span>
                    </div>
                </template>

            </template>

        </v-server-table>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        props: {
            assetType: {
                type: String,
                default: null,
            },

            assetId: {
                type: Number,
                default: null,
            },

            allowFilterSaving: {
                type: Boolean,
                default: false,
            },

            filterId: {
                type: Number,
                default: null,
            },

            dashboardFilters: {
                type: Object,
                default(){
                    return null;
                },
            },

            tableConfig: {
                type: Object,
                default(){
                    return null;
                },
            },
        },

        data() {
            return {
                userTimezone: this.$store.state.user.timezone,
                exportingTable: false,
                loadCustomFieldDefs: true,
                tableName: "employeesTable",

                filterDefs: {
                    'employee.current_jobsite_id': [],
                    'employee.driver_status_id': [],
                    'employee.subcontractor_id': [],
                    'employee.division_id': [],
                    'employee.position': [],
                    'employee.HireDate' : '',
                    'employee.last_rehire_date': '',
                    'employee.IsActive': [{id: 1, name: 'Active'}],
                    'employee.is_temp_employee': [],
                    'custom_fields': {},
                },

                filterKey: 'employeeFilters',

                logicOptions: [
                    { value: '=', text: '=' },
                    { value: '>', text: '>' },
                    { value: '>=', text: '>=' },
                    { value: '<', text: '<' },
                    { value: '<=', text: '<=' },
                    { value: 'LIKE', text: 'LIKE' },
                ],

                approvedDriverOptions: [
                    {id: '0', name: 'Not Approved Driver'},
                    {id: '1', name: 'Is Approved Driver'}
                ],

                isActiveOptions: [
                    {id: 0, name: 'Inactive'},
                    {id: 1, name: 'Active'}
                ],

                empTypeOptions: [
                    {id: 0, name: 'Permanent Employee'},
                    {id: 1, name: 'Temporary Employee'}
                ],
            }
        },

        watch: {
            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },

            columns(){
                //Check for Jobsite
                if(this.assetType === 'Jobsite')
                {
                    return [
                        'LastName',
                        'jobsite_role',
                        'position',
                        'EmployeeNumber',
                        'IsActive',
                        'driver_status_id',
                        'current_jobsite_id',
                        'division_id',
                        'email',
                        'Phone',
                        'trade',
                        // 'Address',
                        // 'City',
                        // 'State',
                        // 'Zip'
                    ];
                }
                return [
                    'LastName',
                    'position',
                    'EmployeeNumber',
                    'IsActive',
                    'driver_status_id',
                    'current_jobsite_id',
                    'division_id',
                    'email',
                    'Phone',
                    'trade',
                ];
            },

            options(){
                return {
                    headings: {
                        LastName: 'Name',
                        jobsite_role: 'Jobsite Role',
                        position: 'Position',
                        EmployeeNumber: 'Employee Number',
                        IsActive: 'Active',
                        driver_status_id: 'Approved Driver',
                        current_jobsite_id: 'Current Jobsite',
                        division_id: 'Division',
                        email: 'Email',
                        Phone: 'Phone',
                        trade: 'Trade',
                        // Address: 'Address',
                        // City: 'City',
                        // State: 'State',
                        // Zip: 'Zip',
                    },
                    saveState: true,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: this.$root.getVisibleColumnsFromState(this.tableName),
                    sendInitialRequest: (this.filterId ? false : true),
                    orderBy: {
                        ascending: true,
                        column: 'employee.LastName',
                    },
                    responseAdapter: (response) => {
                        //Emit the count
                        this.$emit('empListCount', response.data.count);

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the filters
                        data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));

                        //INclude the asset
                        data.assetType = this.assetType;
                        data.assetId = this.assetId;

                        //Load Custom Fields
                        if(this.loadCustomFieldDefs)
                        {
                            data.load_custom_field = true;
                        }

                        //Send it
                        return data;
                    },
                    uniqueKey: 'EmployeeId',
                };
            },
        },

        methods: {

            refreshTableData(filters) {
                //Set the data
                this.$refs.employeesTable.setPage(1);
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.empTableFilters.resetColVis(this.$refs.employeesTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.empSavedFilters.setFilterParams(this.$refs.employeesTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.empTableFilters.setColVis(this.$refs.employeesTable);
            },

            getFilters(){
                return filterExtractor.extractFilters(this.filters);
            },

            exportTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the Filters
                var filters = filterExtractor.extractFilters(this.filters);

                //Build the payload
                var payload = {
                    filters: filters,
                    query: (this.$refs.employeesTable.getRequestParams()).query,
                    orderBy: this.$refs.employeesTable.options.orderBy.column,
                    ascending: this.$refs.employeesTable.options.orderBy.ascending,
                    table: 'employeesTable',
                    columns: this.$refs.employeesTable.columns,
                    visible_columns: this.$root.getVisibleColumnsFromState(this.tableName),
                    export: true,
                    assetType: this.assetType,
                    assetId: this.assetId,
                    load_custom_fields: true,
                    custom_field_type: 'Employee',
                    //headings: this.$refs.employeesTable.options.headings,
                };

                //Send it
                this.$http.get('/employees/list', payload)
                    .then(response => {
                        //Open the new window
                        //window.open(response.data);

                        //Alert the User
                        this.$toastr.success(response.data);
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.error(err.response.data, 'Failed to Export Employees');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },
        },

        mounted(){
            //Check for Staring Filters
            if(!this.dashboardFilters)
            {
                //Set it
                //this.$refs.incTableFilters.setFilters(this.dashboardFilters);

                //Check for a saved filter
                this.checkForAppliedFilter();

                //Check for Filter ID
                if(this.filterId)
                {
                    this.$refs.empSavedFilters.setSavedFilter(this.filterId);
                }
            }

            //Check for Table Config
            if(this.tableConfig)
            {
                //Set it
                this.$refs.empTableFilters.setColVisFromSavedFilter(this.$refs.employeesTable, this.tableConfig);
            }
        }
    }
</script>
